import {
  Box,
  Grid, Stack, Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Fragment,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  CarouselCardAnimatedStack,
  CarouselCardGrid,
  CarouselSlideBox,
  StyledDurationBadge,
  StyledPortalWrapper,
  TitleTypography
} from "./CarouselCard.styles";
import type {
  CarouselCardBaseProps,
  CarouselCardProps
} from "./CarouselCard.types";
import {
  CardCategories,
  CardCta,
  CardSignature,
  CardType,
  CarouselCardMobile,
  Icon,
  IOpen,
  LinkWithInteractableOverlay,
  StatusBox,
  VideoCover
} from "../../components";
import { LEVEL, STATUS_LO } from "../../consts";
import {
  useAccent,
  useIconStatus
} from "../../hooks";
import { useNavigate, formatDate } from "../../services";
import { formatDuration, hasPassedExpiration, isNotDateInfinite } from "../../utils/datetime";
import { CircularProgress } from "../CircularProgress";
import { TruncatedText } from "../TruncatedText";

export function CarouselCard({
  booked,
  categories,
  coverPublicURL,
  coverVideoPublicURL,
  courseId=undefined,
  disabled,
  duration,
  ecmRegistration,
  editionNumber,
  endDate,
  enrollType,
  expirationDate,
  grandParentId,
  id,
  isAutoSubscribeEnable,
  ecm_specialization,
  ecmType,
  isFavourite,
  isMandatory,
  isTest,
  isSurvey,
  isStandAlone,
  isToj,
  learningObjectType,
  learningObjectTypology,
  madeAttempts,
  percentageOfCompletion,
  parentId,
  rootId=undefined,
  setOpenModalEcm,
  shortDescription,
  small,
  startDate,
  status,
  title,
  canAccessNow,
  isLinkRelative,
  link,
  ...props
} : CarouselCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, i18n } = useTranslation();
  const [openDrawer, setOpenDrawer] = useState<IOpen>({
    label: "",
    openId: "",
    state: false
  });
  const slideBoxRef = useRef<HTMLDivElement | null>(null);
  const isExpired = hasPassedExpiration(expirationDate);
  const accentColor = useAccent({ isExpired, isMandatory, status });
  const iconStatus = useIconStatus({ status });
  const [active, setIsActive]=useState(false);
  const navigate = useNavigate();

  const durationNumber = parseInt(`${duration}`);

  const messageStatusBox = (isExpired && expirationDate && status !== "C" && isMandatory )
    ? `${ t("expired") }`
    : undefined;

  const categoryTags = categories.slice(0,2)
    .slice(0, 2)
    .concat(categories.length > 2 ? [`+${categories.length - 2}`] : []);

  const singleCategoryTags = categories.slice(0,1)
    .slice(0, 1)
    .concat(categories.length > 1 ? [`+${categories.length - 1}`] : []);

  const dateFormatted = () =>  {
    if(!startDate ){
      if(
        learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
        || learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS
      ) {
        return   {
          date:"",
          end:"",
          type:"editions"
        };
      } else if (expirationDate){
        return {
          date: formatDate( expirationDate.join("/"), i18n.language),
          end:"",
          type:"normal"
        };
      }  else {
        return   {
          date:"",
          end:"",
          type:"normal"
        };
      }
    }
    else if (endDate && (startDate.join("/") !== endDate.join("/"))) {
      return {
        date: formatDate(startDate.join("/"), i18n.language),
        end:formatDate(endDate.join("/"), i18n.language),
        type: ecm_specialization || isToj ? "ecm_2" :"register_2"
      };
    }
    else {
      return {
        date: formatDate( startDate.join("/"), i18n.language),
        end:"",
        type: ecm_specialization || isToj ? "ecm" :"register"
      };
    }
  };

  const labelRegister1 = `${dateFormatted().date}`;
  const labelRegister2 = `${t("from")} ${dateFormatted().date} ${t("to")} ${dateFormatted().end}`;

  const labelEcm1 = `${dateFormatted().date} `;

  const labelEcm2= `${t("from")} ${dateFormatted().date} ${t("to")} ${dateFormatted().end}`;

  const slideHeight = 378;
  const slideHeightSmall = 315;
  const slideWidth = 506;
  const slideWidthSmall = 406;
  const portalHeight = small ? slideHeightSmall : slideHeight;
  const portalWidth = small ? slideWidthSmall : slideWidth;

  return (
    <Fragment >
      <CarouselSlideBox
        tabIndex={ -1 }
        className={ "" }
        $small={ small }
        $disabled={ false }
        ref={ slideBoxRef }
        onMouseOver={ () => setIsActive(true) }
        onMouseOut={ () =>  setIsActive(false) }
      >
        {
          <CarouselCardGrid
            tabIndex={ -1 }
            sx={ active?{  opacity: 0 }:{ transition: "opacity .2s ease-out" } }
            borderRadius={ undefined }
            component="section"
            container
            direction="column-reverse"
            draggable={ true }
            $coverPublicURL={ coverPublicURL }
            $noHover={ true  }
            $small={ small }

            onClick={ ()=> {
              isMobile && setOpenDrawer({
                label: title,
                openId: id.toString(),
                state: true
              });
            } }
            padding={
              small
                ? theme.spacing(2, 2, 2, 5)
                : theme.spacing(0, 5,3)

            }
            { ...props }
          >
            <Grid
              item
              container
              direction="column"
              gap={ isMobile ? 0 : 1 }
              sx={ {
                overflow:"hidden"
              } }
            >
              {
                duration && !isNaN(durationNumber) && durationNumber > 0 ?
                  <StyledDurationBadge
                    top={ isMobile ? theme.spacing(2) : theme.spacing(3) }
                    right={ small ? "1rem" : "1.5rem" }
                    maxHeight={ isMobile ? "24px" : "auto" }
                    $completed={ status === STATUS_LO.C }>
                    <Icon aria-hidden="true" icon={ "Icons_time" } size={ isMobile ? 15 : 24 }/>
                    { duration && formatDuration(duration, t) }
                  </StyledDurationBadge> : null
              }
              { (status === "C" || isMandatory ) &&
                <StatusBox
                  message={ messageStatusBox }
                  accent={ accentColor }
                  icon={ iconStatus }
                />
              }
              <CardType
                ecm_specialization={ ecm_specialization }
                ecmType={ ecmType }
                fontSize={ small ? "0.875rem" :  isMobile ? "0.75rem" : "1rem" }
                isTest={  Boolean(isTest) }
                isSurvey={ Boolean(isSurvey) }
                color={ theme.customColors.exploreCardText }
                lineHeight={ isMobile ? "0.875" : 3 / 2 }
                learningObjectType={ learningObjectType }
                learningObjectTypology={ learningObjectTypology }
              />
              <TitleTypography
                aria-label={ title }
                fontSize={ (isMobile || small) ? "1.25rem" : "1.5rem" }
                lineHeight={ isMobile? "1.05" : 5 / 4 }
                $clamp={ isMobile || small ? 1 : 2 }
              >
                <TruncatedText text={ title } />
              </TitleTypography>
              <CardCategories
                categories={ singleCategoryTags }
                sx={ {
                  [theme.breakpoints.down("sm")]:{
                    marginTop: "8px"
                  }
                } }
              />

              { /* date */
                isNotDateInfinite(dateFormatted().date) ?
                  <Stack
                    alignItems="center"
                    direction="row"
                    gap={ 1 }
                    height={ "fit-content" }
                  >
                    <Icon
                      icon={ "Icons_scadenza-e-edizioni" }
                      color={ theme.customColors.exploreCardText }
                      size={ isMobile ? 17 : 24 }
                    />
                    <Typography
                      sx={ {
                        [theme.breakpoints.down("sm")]: {
                          fontSize: "0.875rem",
                          lineHeight: "1.125rem"
                        },
                        color: theme.customColors.exploreCardText
                      } }
                    >
                      {
                        dateFormatted().type === "register_2"
                          ? labelRegister2
                          : dateFormatted().type === "register"
                            ? labelRegister1 :
                            dateFormatted().type === "ecm_2"
                              ? labelEcm2
                              : dateFormatted().type === "ecm"
                                ? labelEcm1
                                : `${isExpired ? t("expired_on") : t("expiring_on")} ${dateFormatted().date}`
                      }
                    </Typography>
                  </Stack> :
                  null
              }

              {
                !isMobile ? (
                  <LinkWithInteractableOverlay
                    $disableLink={ disabled }
                    $hideCta
                    href={ navigate.getDetailLink({
                      id,
                      learningObjectTypology,
                      courseId: courseId,
                      rootId: rootId
                      /*
                      TODO: qui perchè non è così come in altri casi? verificare differenza tra rootId e grandParentId
                      courseId:courseId ?? parentId,
              rootId:rootId ?? grandParentId,
                      * */
                    }) }
                    tabIndex={ -1 }
                  >
                    <TruncatedText text={ title } />
                  </LinkWithInteractableOverlay>
                ) : null
              }
            </Grid>
          </CarouselCardGrid>
        }
        {
          !isMobile ? (
            <StyledPortalWrapper
              tabIndex={ -1 }
              onFocus={ (e: React.FocusEvent<HTMLDivElement>) => {
                if (e.target !== e.currentTarget) {
                  setIsActive && setIsActive(true);
                }
              } }
              onBlur={ (e: React.FocusEvent<HTMLDivElement>) => {
                const parent = e.currentTarget;
                const next = e.relatedTarget as HTMLElement | null;
                if (!next || !parent.contains(next)) {
                  setIsActive && setIsActive(false);
                }
              } }
              role="region"
              aria-label={ learningObjectTypology + " - " + title }
              $accent={ accentColor }
              $small={ Boolean(small) }
              sx={ active ? {
                opacity:1,
                transform:"scale(1)",
                height:portalHeight,
                maxWidth:portalWidth,
                width:portalWidth }:{} }
            >
              <ZoomCard
                accentColor={ accentColor }
                booked={ booked }
                categories={ categoryTags }
                className={ "zoom-section" }
                courseId={ courseId }
                coverPublicURL={ coverPublicURL }
                coverVideoPublicURL={ coverVideoPublicURL }
                disabled={ disabled }
                duration={ duration }
                ecm_specialization={ ecm_specialization }
                ecmRegistration={ ecmRegistration }
                ecmType={ ecmType }
                editionNumber={ editionNumber }
                endDate={ endDate }
                enrollType={ enrollType }
                expirationDate={ expirationDate }
                grandParentId={ grandParentId }
                iconStatus={ iconStatus }
                id={ id }
                isActive={ active }
                isAutoSubscribeEnable={ isAutoSubscribeEnable }
                isFavourite={ isFavourite }
                isMandatory={ isMandatory }
                isStandAlone={ isStandAlone }
                isSurvey={ isSurvey }
                isTest={ isTest }
                isToj={ isToj }
                learningObjectType={ learningObjectType }
                learningObjectTypology={ learningObjectTypology }
                madeAttempts={ madeAttempts }
                messageStatusBox={ messageStatusBox }
                parentId={ parentId }
                percentageOfCompletion={ percentageOfCompletion }
                rootId={ rootId }
                setOpenModalEcm={ setOpenModalEcm }
                shortDescription={ shortDescription }
                small={ small }
                startDate={ startDate }
                status={ status }
                title={ title }
                canAccessNow={ canAccessNow }
                isLinkRelative={ isLinkRelative }
                link={ link }
              />
            </StyledPortalWrapper>
          ) : null }
      </CarouselSlideBox>
      {
        isMobile ? (
          <CarouselCardMobile
            booked={ booked }
            categories={ categoryTags }
            coverPublicURL={ coverPublicURL }
            coverVideoPublicURL={ coverVideoPublicURL }
            courseId={ courseId }
            disabled={ disabled }
            duration={ duration }
            ecmRegistration={ ecmRegistration }
            ecmType={ ecmType }
            editionNumber = { editionNumber }
            endDate={ endDate }
            enrollType={ enrollType }
            expirationDate={ expirationDate }
            grandParentId={ grandParentId }
            iconStatus={ iconStatus }
            isMandatory={ isMandatory }
            id={ id }
            isFavourite={ isFavourite }
            isAutoSubscribeEnable={ isAutoSubscribeEnable }
            isStandAlone={ isStandAlone }
            isSurvey={ isSurvey }
            isTest={ isTest }
            isToj={ isToj }
            ecm_specialization={ ecm_specialization }
            learningObjectType={ learningObjectType }
            learningObjectTypology={ learningObjectTypology }
            madeAttempts={ madeAttempts }
            open={ openDrawer }
            percentageOfCompletion={ percentageOfCompletion }
            parentId={ parentId }
            rootId={ rootId }
            setOpenModalEcm={ setOpenModalEcm }
            setOpen={ setOpenDrawer }
            shortDescription={ shortDescription }
            small={ small }
            startDate={ startDate }
            status={ status }
            title={ title }
            canAccessNow={ canAccessNow }
            isLinkRelative={ isLinkRelative }
            link={ link }
          />
        ) : null
      }
    </Fragment>
  );

}

function ZoomCard({
  accentColor,
  categories,
  courseId,
  coverPublicURL,
  coverVideoPublicURL,
  disabled,
  duration,
  ecmRegistration,
  ecmType,
  editionNumber,
  endDate,
  enrollType,
  expirationDate,
  grandParentId,
  iconStatus,
  id,
  ecm_specialization,
  isMandatory,
  isStandAlone,
  isTest,
  isSurvey,
  isActive,
  isToj,
  learningObjectType,
  learningObjectTypology,
  madeAttempts,
  messageStatusBox,
  parentId,
  percentageOfCompletion,
  rootId,
  setOpenModalEcm,
  startDate,
  status,
  title,
  canAccessNow,
  credits,
  isLinkRelative,
  link
}: CarouselCardBaseProps & {
  accentColor: string
  iconStatus: ReturnType<typeof useIconStatus>
  messageStatusBox?:string
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const clampRef = useRef<HTMLElement|null>(null);

  const navigate = useNavigate();
  const isForYou = window.location.href.includes("per-te");

  const durationNumber = parseInt(`${duration}`);

  return (
    <Box
      sx={ {
        display: "flex",
        height:"100%",
        justifyContent: "space-between",
        position:"relative"
      } }
    >
      <VideoCover
        aria-hidden="true"
        borderRadius={ ".5rem" }
        img={  coverPublicURL }
        url={  coverVideoPublicURL }
        sx={ {
          "&:before": {
            background: `${theme.linearGradients.gradientCarouselCard}`
          }
        } }
      />
      <Grid
        item
        container
        direction="column"
        gap={ 1 }
        padding="0 2.5rem 1.52125rem 2.5rem"
        zIndex={ theme.zIndex.appBar }
        justifyContent={ "flex-end" }
        position={ "relative" }
      >
        <Box
          aria-hidden="true"
          sx={ {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            position: "absolute",
            top: theme.spacing(3),
            right: theme.spacing(3),
            gap: theme.spacing(2)
          } }>
          {
            duration && !isNaN(durationNumber) && durationNumber > 0 ?
              <StyledDurationBadge
                top={ theme.spacing(3) }
                right={  "1.5rem" }
                $absolute={ false }
                $completed={ status === STATUS_LO.C }>
                <Icon aria-hidden="true" icon={ "Icons_time" } size={ 24 }/>
                { duration && formatDuration(duration, t) }
              </StyledDurationBadge> : null
          }

          {
            (
              percentageOfCompletion
              && (status === STATUS_LO.P || status === STATUS_LO.C)
              && (learningObjectTypology === LEVEL.PATH || learningObjectTypology === LEVEL.COURSE)
            )
              ? (
                <CircularProgress percentageOfCompletion={ percentageOfCompletion } />
              ) : null
          }
        </Box>

        { (status === "C" || isMandatory ) &&
          <StatusBox
            message={ messageStatusBox }
            accent={ accentColor }
            icon={ iconStatus }
            inZoomCard={ true }
          />
        }
        <CardType
          color={ theme.customColors.exploreCardText }
          ecmType={ ecmType }
          ecm_specialization={ ecm_specialization }
          isTest={  Boolean(isTest) }
          isSurvey={ Boolean(isSurvey) }
          learningObjectTypology={ learningObjectTypology }
          title={ title }
          learningObjectType={ learningObjectType }
          fontSize={ "1rem" }
          lineHeight={ 3 / 2 }
        />
        <TitleTypography
          tabIndex={ 0 }
          aria-label={ title }
          fontSize="1.5rem"
          lineHeight={ 5 / 4 }
          $clamp={ 2 }
          ref={ clampRef }
        >
          <TruncatedText
            clampRef={ clampRef }
            text={ title }
            disabled={ disabled }
            href={ navigate.getDetailLink({
              courseId, id, isForYou, learningObjectTypology, rootId: rootId ?? grandParentId
            }) }
          />
        </TitleTypography>
        <CardCategories categories={ categories } />
        <CarouselCardAnimatedStack
          $delay="0.2"
          sx={ isActive ? {
            gridTemplateRows: "1fr",
            opacity:1,
            zIndex:theme.zIndex.appBar }:{
            zIndex:theme.zIndex.appBar
          } }
        >
          <CardSignature
            isToj={ isToj }
            color={ theme.customColors.exploreCardText }
            ecm_specialization={ ecm_specialization }
            startDate={ startDate }
            endDate={ endDate }
            editionNumber={ editionNumber }
            alignItems="center"
            gap={ 2 }
            expirationDate={ expirationDate }
            learningObjectTypology={ learningObjectTypology }
            canAccessNow={ canAccessNow }
          />
        </CarouselCardAnimatedStack>
        <CarouselCardAnimatedStack $delay="0.2"
          sx={ isActive ? {
            gridTemplateRows: "1fr",
            opacity:1,
            zIndex:theme.zIndex.appBar }:{
            zIndex:theme.zIndex.appBar
          } }
        >
          {
            isActive ?
              <CardCta
                courseId={ courseId }
                color={ theme.customColors.exploreCardText }
                disabled={ disabled }
                ecm_specialization={ ecm_specialization }
                ecmRegistration={ ecmRegistration }
                enrollType={ enrollType }
                grandParentId={ grandParentId }
                id={ id }
                isCard={ true }
                isStandAlone={ isStandAlone }
                isSurvey={ isSurvey }
                isTest={ isTest }
                learningObjectType={ learningObjectType }
                learningObjectTypology={ learningObjectTypology }
                madeAttempts={ madeAttempts }
                parentId={ parentId }
                percentageOfCompletion={ percentageOfCompletion }
                rootId={ rootId }
                setOpenModalEcm={ setOpenModalEcm }
                status={ status }
                title={ title }
                canAccessNow={ canAccessNow }
                isLinkRelative={ isLinkRelative }
                link={ link }
                credits={ credits }
                showDetailsButton={ true }
                ctaPosition={ "split" }
                sx={ {
                  justifyContent:"flex-end",
                  ".MuiStack-root":{
                    justifyContent:"flex-end"
                  }
                } }
              /> : null
          }

        </CarouselCardAnimatedStack>
        <LinkWithInteractableOverlay
          $disableLink={ disabled }
          $hideCta
          href={ navigate.getDetailLink({
            courseId: courseId ?? parentId, id, isForYou, learningObjectTypology, rootId: rootId ?? grandParentId
          }) }
        >
          <TruncatedText text={ title } />
        </LinkWithInteractableOverlay>
      </Grid>

    </Box>

  );
}
